import { Chip, useDisclosure } from "@heroui/react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Billing } from "@/api/billing";
import { EditBillModal } from "../modals/bill/editBillModal";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { ActualPayment } from "@/api/analytics";
import { PromptPaymentModal } from "./modals/promptPaymentModal";

export function FinanceDepositChip({
  displayAmount,
  isPaid,
  rentId,
  onClickCallback,
  fallbackCallback,
}: {
  displayAmount: string;
  isPaid: boolean;
  rentId: number;
  onClickCallback: (rentId: number) => void;
  fallbackCallback?: () => void;
}) {
  const hasDeposit = displayAmount !== "0.00" && parseFloat(displayAmount) > 0;

  const handleClick = useCallback(() => {
    if (hasDeposit) {
      onClickCallback(rentId);
    } else if (fallbackCallback) {
      fallbackCallback();
    }
  }, [hasDeposit, onClickCallback, rentId, fallbackCallback]);

  if (!hasDeposit && !fallbackCallback) {
    return (
      <Chip variant="dot" color="default">
        {displayAmount}
      </Chip>
    );
  }

  return (
    <Chip
      className="cursor-pointer"
      onClick={handleClick}
      variant="dot"
      color={hasDeposit ? (isPaid ? "success" : "warning") : "default"}
      classNames={
        !hasDeposit
          ? {
              dot: "bg-zinc-900 dark:bg-zinc-800",
            }
          : undefined
      }>
      {displayAmount}
    </Chip>
  );
}

export function FinancePaymentChip({
  displayAmount,
  paymentId,
  plannedAmount,
  actualPayments,
  rentId,
  openPromptPaymentCallback = () => {},
  fallbackCallback = () => {},
}: {
  displayAmount: string;
  paymentId: number;
  plannedAmount: number;
  rentedPrice?: number;
  actualPayments?: ActualPayment[];
  rentId: number;
  openPromptPaymentCallback?: (rentId: number, item: ActualPayment[]) => void;
  fallbackCallback?: () => void;
}) {
  const [item, setItem] = useState<Billing | null>(null);

  const items = useAppSelector((state) => state.handbooks.payments);
  const deposits = useAppSelector((state) => state.handbooks.deposits);

  useEffect(() => {
    if (paymentId === -1) {
      setItem(null);
    } else {
      setItem(
        items.find((el) => el.id === paymentId) ||
          deposits.find((el) => el.id === paymentId) ||
          null,
      );
    }
  }, [paymentId, items, deposits]);

  const handleViewOpen = useCallback(() => {
    openPromptPaymentCallback(rentId, actualPayments ?? []);
  }, [openPromptPaymentCallback, rentId, actualPayments]);

  return (
    <>
      {paymentId === -1 && (
        <Chip variant="dot" color="danger">
          удалено
        </Chip>
      )}
      {paymentId !== -1 && item !== null && (
        <Chip
          className="cursor-pointer"
          onClick={handleViewOpen}
          variant="dot"
          color={
            plannedAmount != -1
              ? item.paid
                ? +displayAmount !== plannedAmount
                  ? "warning"
                  : "success"
                : "default"
              : "primary"
          }>
          {displayAmount}
        </Chip>
      )}
      {paymentId !== -1 && item === null && (
        <Chip
          variant="dot"
          classNames={{
            dot: "bg-zinc-900 dark:bg-zinc-800",
          }}
          className="cursor-pointer"
          onClick={fallbackCallback}>
          {displayAmount}
        </Chip>
      )}
    </>
  );
}

export function PaymentChip({
  displayAmount,
  paymentId,
  plannedAmount,
  actualPayments,
  fallbackCallback = () => {},
  openCallback = () => {},
}: {
  displayAmount: string;
  paymentId: number;
  plannedAmount: number;
  rentedPrice?: number;
  actualPayments?: ActualPayment[];
  fallbackCallback?: () => void;
  openCallback?: () => void;
}) {
  const [item, setItem] = useState<Billing | null>(null);
  const [customItem, setCustomItem] = useState<Billing | null>(null);

  const disclosure = useDisclosure();
  const customViewDisclosure = useDisclosure();
  const promptPaymentDisclosure = useDisclosure();

  const items = useAppSelector((state) => state.handbooks.payments);
  const deposits = useAppSelector((state) => state.handbooks.deposits);

  const prevIsOpenRef = useRef(disclosure.isOpen);
  const prevIsCustomOpenRef = useRef(customViewDisclosure.isOpen);

  useEffect(() => {
    if (paymentId === -1) {
      setItem(null);
    } else {
      setItem(
        items.find((el) => el.id === paymentId) ||
          deposits.find((el) => el.id === paymentId) ||
          null,
      );
    }
  }, [paymentId, items, deposits]);

  useEffect(() => {
    if (prevIsOpenRef.current && !disclosure.isOpen) {
      openCallback();
      loadHandbooks({ handbooks: ["billings"] });
    }

    prevIsOpenRef.current = disclosure.isOpen;
  }, [disclosure.isOpen, openCallback]);

  useEffect(() => {
    if (prevIsCustomOpenRef.current && !customViewDisclosure.isOpen) {
      openCallback();
    }

    prevIsCustomOpenRef.current = customViewDisclosure.isOpen;
  }, [customViewDisclosure.isOpen, openCallback]);

  const handleViewOpen = useCallback(() => {
    promptPaymentDisclosure.onOpen();
  }, [actualPayments]);

  const handlePaymentSelection = useCallback(
    (payment: ActualPayment) => {
      setCustomItem(items.find((el) => el.id == payment.billing_id) || null);
      promptPaymentDisclosure.onClose();
      customViewDisclosure.onOpen();
    },
    [items],
  );

  useEffect(() => {
    if (!customViewDisclosure.isOpen) {
      setCustomItem(null);
    }
  }, [customViewDisclosure.isOpen]);

  const handleCreatePayment = useCallback(() => {
    fallbackCallback();
    promptPaymentDisclosure.onClose();
  }, [fallbackCallback, promptPaymentDisclosure]);

  return (
    <>
      <PromptPaymentModal
        disclosure={promptPaymentDisclosure}
        payments={actualPayments}
        selectPayment={handlePaymentSelection}
        createPayment={handleCreatePayment}
      />
      <EditBillModal disclosure={customViewDisclosure} billing={customItem} />
      <EditBillModal disclosure={disclosure} billing={item} />
      {paymentId === -1 && (
        <Chip variant="dot" color="danger">
          удалено
        </Chip>
      )}
      {paymentId !== -1 && item !== null && (
        <Chip
          className="cursor-pointer"
          onClick={handleViewOpen}
          variant="dot"
          color={
            plannedAmount != -1
              ? item.paid
                ? +displayAmount !== plannedAmount
                  ? "warning"
                  : "success"
                : "default"
              : "primary"
          }>
          {displayAmount}
        </Chip>
      )}
      {paymentId !== -1 && item === null && (
        <Chip
          variant="dot"
          classNames={{
            dot: "bg-zinc-900 dark:bg-zinc-800",
          }}
          className="cursor-pointer"
          onClick={fallbackCallback}>
          {displayAmount}
        </Chip>
      )}
    </>
  );
}
